import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const motionPreference = '(prefers-reduced-motion: reduce)';
const arrivalEase = 'cubic-bezier(0.16, 1, 0.3, 1)';

// Content is visible by default. Observation only adds a one-time arrival.
export function useScrollReveal<T extends HTMLElement>(revision = '', selector = '[data-reveal]') {
  const root = useRef<T>(null);

  useEffect(() => {
    const element = root.current;
    if (!element || !('IntersectionObserver' in window)) return;
    const preference = window.matchMedia(motionPreference);
    const animations = new Set<Animation>();
    let observer: IntersectionObserver | undefined;

    const stop = () => {
      observer?.disconnect();
      animations.forEach(animation => animation.cancel());
      animations.clear();
    };
    const start = () => {
      stop();
      if (preference.matches) return;
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          observer?.unobserve(entry.target);
          const target = entry.target as HTMLElement;
          if (!target.animate) return;
          const row = target.dataset.reveal === 'row';
          const animation = target.animate([
            { opacity: 0.3, transform: row ? 'translateX(14px)' : 'translateY(18px)' },
            { opacity: 1, transform: 'none' },
          ], {
            duration: row ? 420 : 500,
            delay: Math.min(Number(target.dataset.revealOrder) || 0, 4) * 30,
            easing: arrivalEase,
          });
          animations.add(animation);
          animation.onfinish = () => animations.delete(animation);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -24px 0px' });
      element.querySelectorAll(selector).forEach(target => observer?.observe(target));
    };

    start();
    preference.addEventListener('change', start);
    return () => { stop(); preference.removeEventListener('change', start); };
  }, [revision, selector]);

  return root;
}

export function RouteMotion({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return <div className="route-view" key={pathname}>{children}</div>;
}

export function PageLoading() {
  return <div className="page-loading" role="status">
    <svg className="loading-mark" width="48" height="48" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 5h4v5.2L14.5 5h5L12.2 12 20 19h-5.3L9 13.8V19H5Z" fill="currentColor" />
    </svg>
    <span className="loading-track" aria-hidden="true" />
    <span>Loading article…</span>
  </div>;
}

export class RouteBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() { return { failed: true }; }

  render() {
    if (!this.state.failed) return this.props.children;
    return <div className="page-loading route-error" role="alert">
      <h1>This page couldn't load.</h1>
      <p>Check your connection and try again.</p>
      <button className="button-primary" type="button" onClick={() => window.location.reload()}>Reload page</button>
    </div>;
  }
}

export function ReadingProgress() {
  const { pathname } = useLocation();
  const bar = useRef<HTMLDivElement>(null);
  const reading = pathname.startsWith('/blog/');

  useEffect(() => {
    if (!reading) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const distance = document.documentElement.scrollHeight - window.innerHeight;
      const progress = distance > 0 ? Math.min(1, Math.max(0, window.scrollY / distance)) : 0;
      if (bar.current) bar.current.style.transform = `scaleX(${progress})`;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(document.body);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [pathname, reading]);

  return reading ? <div className="reading-progress" aria-hidden="true"><div ref={bar} /></div> : null;
}

type Click = { id: number; x: number; y: number };

export function ClickFeedback() {
  const [clicks, setClicks] = useState<Click[]>([]);
  const sequence = useRef(0);

  useEffect(() => {
    const preference = window.matchMedia(motionPreference);
    const clear = () => setClicks([]);
    const acknowledge = (event: MouseEvent) => {
      if (preference.matches || event.button !== 0 || !(event.target instanceof Element)) return;
      const target = event.target.closest('button:not(:disabled), a[href], summary');
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const click = {
        id: ++sequence.current,
        x: event.detail ? event.clientX : rect.left + rect.width / 2,
        y: event.detail ? event.clientY : rect.top + rect.height / 2,
      };
      setClicks(previous => [...previous.slice(-3), click]);
    };
    document.addEventListener('click', acknowledge, true);
    preference.addEventListener('change', clear);
    return () => {
      document.removeEventListener('click', acknowledge, true);
      preference.removeEventListener('change', clear);
    };
  }, []);

  return <div className="click-feedback" aria-hidden="true">{clicks.map(click => <span
    key={click.id}
    style={{ left: click.x, top: click.y }}
    onAnimationEnd={() => setClicks(previous => previous.filter(item => item.id !== click.id))}
  />)}</div>;
}
